import React, { useEffect } from 'react';
import { useFormState } from 'react-hook-form';
import { useSelector } from 'react-redux';
import {
  Disclaimer,
  FormInput,
  FormLabel,
  FormSelect,
  FormTextArea,
  ProductForm,
} from './requiredProductFormStyles';
import {
  cartItemsWithRequiredFieldsSelector,
  personalizationSetToSameValueSelector,
  requiredFieldValuesByIdSelector,
} from '../../../../selectors';

const RequiredProductForm = ({ control, register, setValue }) => {
  const requiredFields = useSelector(
    (state) => state.productConfig.currentProduct?.required_product_fields,
  );
  const { errors } = useFormState({ control });
  const personalizationSetToSameValue = useSelector(
    personalizationSetToSameValueSelector,
  );
  const existingCartItemsWithRequiredFields = useSelector(
    cartItemsWithRequiredFieldsSelector,
  );
  const requiredFieldValuesById = useSelector(requiredFieldValuesByIdSelector);

  const incomingKeys = new Set(
    (requiredFields || []).map((f) => f.key).filter(Boolean),
  );
  const cartItemsWithOverlappingFields =
    existingCartItemsWithRequiredFields.filter((item) =>
      (item.requiredFields || []).some((f) => incomingKeys.has(f.key)),
    );

  useEffect(() => {
    if (!personalizationSetToSameValue || !setValue) return;
    (requiredFields || []).forEach((field) => {
      const existingValue = requiredFieldValuesById[field.id];
      if (existingValue != null && existingValue !== '') {
        setValue(field.key, existingValue);
      }
    });
  }, [
    personalizationSetToSameValue,
    setValue,
    requiredFieldValuesById,
    requiredFields,
  ]);

  if (!requiredFields || requiredFields.length <= 0) {
    return null;
  }

  const renderInput = (field) => {
    const {
      key: fieldKey,
      placeholder,
      field_type: fieldType,
      label,
      max_chars: maxChars,
      invalid_chars: invalidChars,
    } = field;

    const validateField = (value) => {
      if (!value || value.trim().length === 0) {
        return 'This field is required';
      }

      if (invalidChars) {
        const invalidCharsArray = invalidChars
          .split(' ')
          .map((char) => char.trim());
        const foundInvalidChars = invalidCharsArray.filter((char) =>
          value.includes(char),
        );
        if (foundInvalidChars.length > 0) {
          return `contains invalid characters: ${foundInvalidChars.join(', ')}`;
        }
      }

      if (maxChars) {
        if (fieldType === 'integer' || fieldType === 'decimal') {
          const digitCount = value.replace(/[^0-9]/g, '').length;
          if (digitCount > maxChars) {
            return `exceeds maximum of ${maxChars} digits`;
          }
        } else if (value.length > maxChars) {
          return `exceeds maximum length of ${maxChars} characters`;
        }
      }

      return true;
    };

    const inputFields = {
      placeholder,
      name: fieldKey,
      id: fieldKey,
    };
    switch (fieldType) {
      case 'text':
        return (
          <FormTextArea
            {...inputFields}
            {...register(fieldKey, {
              required: `${label} is required`,
              validate: validateField,
            })}
          />
        );
      case 'date':
        return (
          <FormInput
            {...inputFields}
            type="date"
            {...register(fieldKey, {
              required: `${label} is required`,
              validate: validateField,
            })}
          />
        );
      case 'boolean':
        return (
          <FormSelect
            {...inputFields}
            {...register(fieldKey, {
              required: `${label} is required`,
              validate: validateField,
            })}
          >
            <option value>true</option>
            <option value={false}>false</option>
          </FormSelect>
        );
      case 'string':
        return (
          <FormInput
            {...inputFields}
            type="text"
            {...register(fieldKey, {
              required: `${label} is required`,
              validate: validateField,
            })}
          />
        );
      case 'integer':
        return (
          <FormInput
            {...inputFields}
            type="number"
            step="1"
            {...register(fieldKey, {
              required: `${label} is required`,
              validate: validateField,
            })}
          />
        );
      case 'decimal':
        return (
          <FormInput
            {...inputFields}
            type="number"
            step="0.01"
            {...register(fieldKey, {
              required: `${label} is required`,
              validate: validateField,
            })}
          />
        );
      default:
        return (
          <FormInput
            {...inputFields}
            type={fieldType}
            {...register(fieldKey, {
              required: `${label} is required`,
              validate: validateField,
            })}
          />
        );
    }
  };

  return (
    <div>
      {personalizationSetToSameValue &&
        cartItemsWithOverlappingFields.length > 0 && (
          <Disclaimer>
            <div>
              These values will be applied to all products in your order. The
              following products will have their values updated to match:
            </div>
            <ul>
              {cartItemsWithOverlappingFields.map((item) => (
                <li key={item.id}>{item.productName}</li>
              ))}
            </ul>
          </Disclaimer>
        )}
      <ProductForm>
        {requiredFields.map((field) => (
          <div key={`${field.key}-${field.field_type}`}>
            <FormLabel
              isInline={field.field_type === 'string'}
              htmlFor={field.key}
            >{`${field.label}:`}</FormLabel>
            {renderInput(field)}
            {/* <Controller
              name={field.name}
              control={control}
              defaultValue=""
              render={() => renderInput(field)}
              rules={
                field.field_type !== 'string' && {
                  required: `${field.label} is required.`,
                }
              }
            /> */}
            {errors[field.key] && (
              <p className="error--red">{errors[field.key].message}</p>
            )}
          </div>
        ))}
      </ProductForm>
    </div>
  );
};

export default RequiredProductForm;
